import React from 'react';
import { useBatteryStore } from '../../store/batteryStore';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Check, X, AlertTriangle, Zap, Minus } from 'lucide-react';

export const ElectricalRouting = () => {
  const { selectedResult, cells, selectedCellId } = useBatteryStore();
  const cell = cells.find(c => c.id === selectedCellId);
  
  if (!selectedResult) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500" data-testid="routing-empty">
        <div className="text-center">
          <div className="font-heading text-sm uppercase tracking-wider mb-1">No Configuration</div>
          <div className="text-xs">Generate and select a pack to view routing</div>
        </div>
      </div>
    );
  }
  
  const config = selectedResult.config;
  const validation = selectedResult.validation;
  
  // Generate grid representation
  const generateGrid = (isTop) => {
    const grid = [];
    const cellsPerRow = config.parablock_cols;
    
    for (let s = 0; s < config.series; s++) {
      const row = [];
      for (let p = 0; p < config.parallel; p++) {
        row.push({
          seriesGroup: s,
          parallelIndex: p,
          isTop,
          // Simplified connection logic
          hasParallelConnection: p < config.parallel - 1,
          hasSeriesConnection: isTop ? (s < config.series - 1 && p === config.parallel - 1) : (s > 0 && p === 0),
        });
      }
      grid.push(row);
    }
    
    return grid;
  };
  
  const topGrid = generateGrid(true);
  const bottomGrid = generateGrid(false);
  
  const GridCell = ({ cell, isTop }) => {
    const bgColor = isTop ? 'bg-red-500/20' : 'bg-blue-500/20';
    const borderColor = isTop ? 'border-red-500/40' : 'border-blue-500/40';
    
    return (
      <div className="relative">
        <div 
          className={`w-8 h-8 rounded-full ${bgColor} border ${borderColor} flex items-center justify-center text-[8px] font-mono`}
        >
          <span className="text-zinc-400">
            {cell.seriesGroup + 1}.{cell.parallelIndex + 1}
          </span>
        </div>
        
        {/* Parallel connection (horizontal line) */}
        {cell.hasParallelConnection && (
          <div 
            className="absolute top-1/2 -right-2 w-4 h-0.5 bg-cyan-500/60"
            style={{ transform: 'translateY(-50%)' }}
          />
        )}
        
        {/* Series connection marker */}
        {cell.hasSeriesConnection && (
          <div 
            className={`absolute ${isTop ? '-bottom-2' : '-top-2'} left-1/2 w-0.5 h-4 bg-orange-500`}
            style={{ transform: 'translateX(-50%)' }}
          />
        )}
      </div>
    );
  };
  
  return (
    <div className="h-full flex" data-testid="electrical-routing">
      {/* Routing Grids */}
      <div className="flex-1 p-4 space-y-6 overflow-auto">
        {/* Top View */}
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-red-400 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Top View (Positive Terminals)
          </h3>
          <div className="bg-zinc-800/30 rounded-lg p-4">
            <div className="flex flex-col gap-3">
              {topGrid.map((row, rowIndex) => (
                <div key={rowIndex} className="flex gap-3 items-center">
                  <span className="w-8 text-xs font-mono text-zinc-500">S{rowIndex + 1}</span>
                  <div className="flex gap-3">
                    {row.map((cell, colIndex) => (
                      <GridCell key={colIndex} cell={cell} isTop={true} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-4 text-[10px] text-zinc-500">
              <div className="flex items-center gap-1">
                <div className="w-4 h-0.5 bg-cyan-500/60" />
                <span>Parallel</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-0.5 h-4 bg-orange-500" />
                <span>Series</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom View */}
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-blue-400 mb-3 flex items-center gap-2">
            <Minus className="w-4 h-4" />
            Bottom View (Negative Terminals)
          </h3>
          <div className="bg-zinc-800/30 rounded-lg p-4">
            <div className="flex flex-col gap-3">
              {bottomGrid.map((row, rowIndex) => (
                <div key={rowIndex} className="flex gap-3 items-center">
                  <span className="w-8 text-xs font-mono text-zinc-500">S{rowIndex + 1}</span>
                  <div className="flex gap-3">
                    {row.map((cell, colIndex) => (
                      <GridCell key={colIndex} cell={cell} isTop={false} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Connection Legend */}
        <div className="bg-zinc-800/20 rounded-lg p-4">
          <h4 className="font-heading text-xs uppercase tracking-wider text-zinc-400 mb-2">
            Connection Guide
          </h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500/30 border border-red-500/50" />
              <span className="text-zinc-400">Positive terminal (top)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500/30 border border-blue-500/50" />
              <span className="text-zinc-400">Negative terminal (bottom)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-0.5 bg-cyan-500" />
              <span className="text-zinc-400">Parallel connection (same group)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-0.5 h-4 bg-orange-500" />
              <span className="text-zinc-400">Series connection (between groups)</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Info Panel */}
      <div className="w-64 border-l border-zinc-800 p-4 space-y-4">
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Status
          </h3>
          <div className={`p-3 rounded-lg ${
            validation.is_valid 
              ? 'bg-green-500/10 border border-green-500/30' 
              : 'bg-red-500/10 border border-red-500/30'
          }`}>
            <div className="flex items-center gap-2">
              {validation.is_valid ? (
                <>
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-400">Valid Pack</span>
                </>
              ) : (
                <>
                  <X className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-400">Invalid Pack</span>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Validation
          </h3>
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Blocks Balanced</span>
              {validation.blocks_balanced ? (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Yes</Badge>
              ) : (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">No</Badge>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">All Connected</span>
              {validation.all_cells_connected ? (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Yes</Badge>
              ) : (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">No</Badge>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">No Overlap</span>
              {validation.no_overlapping ? (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Yes</Badge>
              ) : (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">No</Badge>
              )}
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            Configuration
          </h3>
          <div className="space-y-2 text-xs font-mono">
            <div className="flex justify-between">
              <span className="text-zinc-500">Series:</span>
              <span className="text-zinc-200">{config.series}S</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Parallel:</span>
              <span className="text-zinc-200">{config.parallel}P</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Total Cells:</span>
              <span className="text-zinc-200">{config.series * config.parallel}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Layers:</span>
              <span className="text-zinc-200">{config.layers}</span>
            </div>
          </div>
        </div>
        
        {/* Mini 3D Preview Placeholder */}
        <div>
          <h3 className="font-heading text-sm uppercase tracking-wider text-cyan-400 mb-3">
            3D Preview
          </h3>
          <div className="aspect-square bg-zinc-800/50 rounded-lg flex items-center justify-center text-zinc-600 text-xs">
            <div className="text-center">
              <div className="font-heading uppercase tracking-wider">Mini Preview</div>
              <div className="text-[10px] mt-1">See main viewport</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
